import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Heart, Share2, Bed, Bath, Square, Calendar, 
  Car, MapPin, Phone, Mail, ChevronLeft, ChevronRight, CalendarCheck,
  MessageCircle, MessageSquare, Copy, Facebook, Twitter, Sparkles
} from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { FixedBottomBarPortal } from '@/components/layout/FixedBottomBarPortal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { ContactForm } from '@/components/property/ContactForm';
import { MortgageCalculator } from '@/components/property/MortgageCalculator';
import { useProperty, useFeaturedProperties } from '@/hooks/useProperties';
import { useAuth } from '@/contexts/AuthContext';
import { useIsFavorite, useToggleFavorite } from '@/hooks/useFavorites';
import { useTrackView } from '@/hooks/useRecentlyViewed';
import { PropertyCard } from '@/components/property/PropertyCard';
import { MarketingPostGenerator } from '@/components/property/MarketingPostGenerator';
import { ViewingPlanCTA } from '@/components/ViewingPlanCTA';
import { formatPrice, formatNumber, formatDate, getPropertyImage, calculateDaysOnMarket, formatDaysOnMarket } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  PropertyPageMeta, 
  RealEstateListingSchema, 
  BreadcrumbSchema 
} from '@/components/seo/StructuredData';

// Component for showing suggestions when a property is not found
function PropertyNotFoundSuggestions() {
  const { data: featuredProperties, isLoading } = useFeaturedProperties(undefined, 6);
  
  if (isLoading || !featuredProperties?.length) return null;
  
  return (
    <div className="border-t pt-12">
      <h2 className="text-2xl font-semibold text-center mb-8">
        Check Out These Available Properties
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {featuredProperties.slice(0, 6).map((property) => (
          <PropertyCard key={property.id} property={property} />
        ))}
      </div>
    </div>
  );
}

export default function PropertyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: property, isLoading } = useProperty(id!);
  const { data: similarProperties } = useFeaturedProperties(property?.city, 3);
  const { data: isFavorite } = useIsFavorite(id!);
  const toggleFavorite = useToggleFavorite();
  const trackView = useTrackView();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showMarketingPost, setShowMarketingPost] = useState(false);
  const contactFormRef = useRef<HTMLDivElement>(null);

  // Redirect UUID URLs to slug URLs for SEO
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id || '');
  useEffect(() => {
    if (property?.slug && isUuid) {
      navigate(`/property/${property.slug}`, { replace: true });
    }
  }, [property?.slug, isUuid, navigate]);

  const handleBack = () => navigate(-1);

  const scrollToContactForm = () => {
    contactFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Track view when property loads
  useEffect(() => {
    if (property && user) {
      trackView.mutate(property.id);
    }
  }, [property?.id, user]);

  const handleFavorite = () => {
    if (!user) {
      toast.error('Please sign in to save properties');
      return;
    }
    toggleFavorite.mutate({ propertyId: id!, isFavorite: !!isFavorite });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = property?.title || 'Check out this property';
  const shareText = property ? `${property.title} - ${formatPrice(property.price)} | ${property.bedrooms} bed, ${property.bathrooms} bath` : '';

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard');
  };

  const handleShareWhatsApp = () => {
    const message = encodeURIComponent(`${shareText}\n${shareUrl}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const handleShareSMS = () => {
    const message = encodeURIComponent(`${shareText}\n${shareUrl}`);
    window.open(`sms:?body=${message}`, '_self');
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(shareTitle);
    const body = encodeURIComponent(`${shareText}\n\nView property: ${shareUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_self');
  };

  const handleShareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
  };

  const handleShareTwitter = () => {
    const text = encodeURIComponent(shareText);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`, '_blank', 'width=600,height=400');
  };

  const handleCopyForSocial = async (platform: string) => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success(`Link copied! Paste it in ${platform}`);
  };

  const images = property?.images || [];
  const nextImage = () => setCurrentImageIndex((i) => (i + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((i) => (i - 1 + images.length) % images.length);

  // Generate SEO-optimized meta description
  const generateMetaDescription = () => {
    if (!property) return '';
    const parts = [
      `${property.bedrooms} bed, ${property.bathrooms} bath`,
      property.sqft ? `${formatNumber(property.sqft)} sqft` : null,
      property.property_type,
      `in ${property.neighborhood || property.city}`,
      `Listed at ${formatPrice(property.price)}.`,
      'Book a viewing online today!'
    ].filter(Boolean);
    return parts.join(' ');
  };

  // Generate SEO title
  const generateSeoTitle = () => {
    if (!property) return 'Property | Book Viewing';
    return `${property.address} – ${property.neighborhood || property.city}, ${property.city} | Book Viewing`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container py-8">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="aspect-[16/9] w-full mb-8" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
            <Skeleton className="h-96 w-full" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <PropertyPageMeta
          title="Listing No Longer Available | Book Viewing"
          description="This property listing is no longer available. Browse other homes for sale in Edmonton and Calgary."
          canonicalUrl={`https://bookviewing.ca/property/${id}`}
          image="https://bookviewing.ca/og-image.png"
          ogImage="https://bookviewing.ca/og-image.png"
          noIndex={true}
        />
        <main className="flex-1 container py-12">
          <div className="max-w-2xl mx-auto text-center mb-12">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
              <MapPin className="w-8 h-8 text-muted-foreground" />
            </div>
            <h1 className="text-3xl font-semibold mb-4">Listing No Longer Available</h1>
            <p className="text-muted-foreground text-lg mb-8">
              This property may have been sold, taken off the market, or the listing has expired. 
              Don't worry – we have plenty of other great homes for you to explore!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/search">
                <Button size="lg" className="w-full sm:w-auto">
                  Browse All Properties
                </Button>
              </Link>
              <Link to="/top-ranked">
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  View Top Ranked Homes
                </Button>
              </Link>
            </div>
          </div>

          {/* Show featured properties as suggestions */}
          <PropertyNotFoundSuggestions />
        </main>
        <Footer />
      </div>
    );
  }

  const canonicalUrl = `https://bookviewing.ca/property/${property.slug || property.id}`;
  const primaryImage = getPropertyImage(images, 0);
  // Use property's primary photo as OG image (most reliable for Facebook)
  const ogImageUrl = primaryImage;
  return (
    <div className="min-h-screen flex flex-col">
      {/* SEO Meta Tags */}
      <PropertyPageMeta
        title={generateSeoTitle()}
        description={generateMetaDescription()}
        canonicalUrl={canonicalUrl}
        image={primaryImage}
        ogImage={ogImageUrl}
        price={property.price}
        address={property.address}
        city={property.city}
        neighborhood={property.neighborhood || undefined}
      />

      {/* Structured Data */}
      <RealEstateListingSchema
        name={property.title}
        description={property.description || generateMetaDescription()}
        url={canonicalUrl}
        image={primaryImage}
        price={property.price}
        address={{
          streetAddress: property.address,
          addressLocality: property.city,
          addressRegion: "AB",
          postalCode: property.postal_code || undefined,
          addressCountry: "CA"
        }}
        numberOfRooms={property.bedrooms || undefined}
        numberOfBathroomsTotal={property.bathrooms || undefined}
        floorSize={property.sqft ? { value: property.sqft, unitCode: "SQF" } : undefined}
        yearBuilt={property.year_built || undefined}
        propertyType={property.property_type}
      />

      <BreadcrumbSchema
        items={[
          { name: "Home", url: "https://bookviewing.ca/" },
          { name: "Search", url: "https://bookviewing.ca/search" },
          { name: property.city, url: `https://bookviewing.ca/search?q=${encodeURIComponent(property.city)}` },
          { name: property.address, url: canonicalUrl }
        ]}
      />

      <Header />

      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="border-b bg-card">
          <div className="container py-3">
            <button onClick={handleBack} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </button>
          </div>
        </div>

        {/* Image Gallery */}
        <div className="relative bg-muted">
          <div className="container py-4">
            <div className="relative aspect-[16/9] md:aspect-[21/9] rounded-xl overflow-hidden">
              <OptimizedImage
                src={getPropertyImage(images, currentImageIndex)}
                alt={`${property.address} - ${property.city} - Photo ${currentImageIndex + 1}`}
                loading={currentImageIndex === 0 ? "eager" : "lazy"}
                fetchPriority={currentImageIndex === 0 ? "high" : "auto"}
                width={1280}
                height={548}
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 1280px"
                className="w-full h-full"
              />
              
              {images.length > 1 && (
                <>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="absolute left-4 top-1/2 -translate-y-1/2"
                    onClick={prevImage}
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="absolute right-4 top-1/2 -translate-y-1/2"
                    onClick={nextImage}
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                  {/* Photo count indicator */}
                  <div className="absolute bottom-4 left-4 bg-background/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-sm font-medium">
                    {currentImageIndex + 1} of {images.length}
                  </div>

                  {/* Dot indicators */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {images.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentImageIndex(i)}
                        aria-label={`Go to image ${i + 1}`}
                        className={cn(
                          "w-2 h-2 rounded-full transition-all",
                          i === currentImageIndex ? "bg-background w-6" : "bg-background/50"
                        )}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Top right actions */}
              <div className="absolute top-4 right-4 flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="icon" aria-label="Share property">
                      <Share2 className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem onClick={handleShareWhatsApp} className="cursor-pointer">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleShareFacebook} className="cursor-pointer">
                      <Facebook className="w-4 h-4 mr-2" />
                      Facebook
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleShareTwitter} className="cursor-pointer">
                      <Twitter className="w-4 h-4 mr-2" />
                      X (Twitter)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleCopyForSocial('Instagram')} className="cursor-pointer">
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                      Copy for Instagram
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleCopyForSocial('TikTok')} className="cursor-pointer">
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                      Copy for TikTok
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleShareSMS} className="cursor-pointer">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Text Message
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleShareEmail} className="cursor-pointer">
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer">
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Link
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setShowMarketingPost(true)} 
                      className="cursor-pointer text-primary font-medium"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Create Marketing Post
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button 
                  variant="secondary" 
                  size="icon" 
                  onClick={handleFavorite}
                  className={cn(isFavorite && "text-destructive")}
                  aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                >
                  <Heart className={cn("w-5 h-5", isFavorite && "fill-current")} />
                </Button>
              </div>

              {/* Prominent Book a Viewing button on gallery */}
              <div className="absolute bottom-4 right-4 hidden md:block">
                <Button 
                  size="lg" 
                  className="gap-2 shadow-lg"
                  onClick={scrollToContactForm}
                >
                  <CalendarCheck className="w-5 h-5" />
                  Book a Viewing
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Property Details */}
        <div className="container py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="md:col-span-2 space-y-8">
              {/* Header - SEO Priority Content Block */}
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <Badge>{property.property_type}</Badge>
                  <Badge variant="outline">{property.status}</Badge>
                </div>
                <h1 className="font-display text-3xl md:text-4xl font-bold mb-2">
                  {property.title}
                </h1>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  <span>{property.address}, {property.city}</span>
                  {property.postal_code && <span>• {property.postal_code}</span>}
                </div>
              </div>

              {/* Price & Key Stats - SEO Priority Content Block */}
              <Card>
                <CardContent className="p-6">
                  <p className="text-price text-3xl md:text-4xl font-bold mb-4">
                    {formatPrice(property.price)}
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Bed className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{property.bedrooms}</p>
                        <p className="text-sm text-muted-foreground">Bedrooms</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Bath className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{property.bathrooms}</p>
                        <p className="text-sm text-muted-foreground">Bathrooms</p>
                      </div>
                    </div>
                    {property.sqft && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Square className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold">{formatNumber(property.sqft)}</p>
                          <p className="text-sm text-muted-foreground">Sq Ft</p>
                        </div>
                      </div>
                    )}
                    {property.year_built && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold">{property.year_built}</p>
                          <p className="text-sm text-muted-foreground">Year Built</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Description - SEO Priority Content */}
              {property.description && (
                <Card>
                  <CardHeader>
                    <CardTitle>About This Property</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground whitespace-pre-line">
                      {property.description}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Features */}
              {property.features && property.features.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Features & Amenities</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {property.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-primary" />
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Property Details */}
              <Card>
                <CardHeader>
                  <CardTitle>Property Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Type</span>
                      <span className="font-medium">{property.property_type}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Status</span>
                      <span className="font-medium">{property.status}</span>
                    </div>
                    {property.lot_size && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">Lot Size</span>
                        <span className="font-medium">{property.lot_size}</span>
                      </div>
                    )}
                    {property.parking && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">Parking</span>
                        <span className="font-medium">{property.parking}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Listed</span>
                      <span className="font-medium">{formatDate(property.listing_date)}</span>
                    </div>
                    {property.neighborhood && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">Community</span>
                        <span className="font-medium">{property.neighborhood}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Agent Details - RECA Compliant */}
              {(property.agent_name || property.brokerage_name) && (
                <Card>
                  <CardHeader>
                    <CardTitle>Agent Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {property.agent_name && (
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-muted-foreground">Primary Agent</span>
                          <span className="font-medium">{property.agent_name}</span>
                        </div>
                      )}
                      {property.brokerage_name && (
                        <div className="flex justify-between py-2 border-b">
                          <span className="text-muted-foreground">Brokerage</span>
                          <span className="font-medium">{property.brokerage_name}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Listing Details */}
              <Card>
                <CardHeader>
                  <CardTitle>Listing Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    {property.mls_id && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">MLS® Number</span>
                        <span className="font-medium">{property.mls_id}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Listed</span>
                      <span className="font-medium">{formatDate(property.listing_date)}</span>
                    </div>
                    {/* Days on Market (DOM) */}
                    {property.listing_date && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-muted-foreground">Days on Market</span>
                        <span className="font-medium">
                          {(() => {
                            const dom = calculateDaysOnMarket(property.listing_date);
                            return dom !== null ? (
                              <span className={dom <= 7 ? 'text-green-600' : dom >= 90 ? 'text-amber-600' : ''}>
                                {formatDaysOnMarket(dom)}
                              </span>
                            ) : '—';
                          })()}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">Source</span>
                      <span className="font-medium">REALTORS® Association of Edmonton</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar - Sticky on tablet and desktop */}
            <div className="sticky top-4 self-start space-y-6">
              {/* Contact Form */}
              <div ref={contactFormRef}>
                <ContactForm property={property} />
              </div>

              {/* Viewing Plan CTA */}
              <ViewingPlanCTA variant="button" />

              {/* Mortgage Calculator */}
              <MortgageCalculator propertyPrice={property.price} />

              {/* Agent Card */}
              {(property.agent_name || property.brokerage_name) && (
                <Card>
                  <CardHeader>
                    <CardTitle>Listed By</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center text-2xl font-semibold flex-shrink-0">
                        {(property.agent_name || property.brokerage_name || 'R').charAt(0)}
                      </div>
                      <div className="min-w-0">
                        {property.agent_name ? (
                          <>
                            <p className="font-semibold truncate">{property.agent_name}</p>
                            {property.brokerage_name && (
                              <p className="text-sm text-muted-foreground truncate">{property.brokerage_name}</p>
                            )}
                          </>
                        ) : property.brokerage_name ? (
                          <p className="font-semibold truncate">{property.brokerage_name}</p>
                        ) : null}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Similar Properties */}
          {similarProperties && similarProperties.length > 0 && (
            <section className="mt-16">
              <h2 className="font-display text-2xl font-semibold mb-6">Similar Properties</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {similarProperties
                  .filter(p => p.id !== property.id)
                  .slice(0, 3)
                  .map(p => (
                    <PropertyCard key={p.id} property={p} />
                  ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />

      {/* Sticky Book a Viewing Bar - ALL screen sizes */}
      <FixedBottomBarPortal>
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t shadow-lg p-4 z-50">
          <div className="container">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-lg font-bold text-price truncate">{formatPrice(property.price)}</p>
                <p className="text-sm text-muted-foreground truncate">{property.address}</p>
              </div>
              <Button size="lg" className="gap-2 flex-shrink-0" onClick={scrollToContactForm}>
                <CalendarCheck className="w-4 h-4" />
                Book a Viewing
              </Button>
            </div>
          </div>
        </div>
      </FixedBottomBarPortal>

      {/* Spacer for sticky bar */}
      <div className="h-20" />



      {/* Marketing Post Generator Modal */}
      <MarketingPostGenerator 
        property={property} 
        open={showMarketingPost} 
        onOpenChange={setShowMarketingPost} 
      />
    </div>
  );
}
